import {
  defaultCategoryId,
  TEMPLATES,
  type DefaultCategoryKey,
  type Item,
  type ItemId,
  type Project,
  type ScenarioId,
  type TemplateId,
} from '@finsim/model';
import { attach, createEvent, sample } from 'effector';
import { $project, commandIssued } from '../../entities/project';
import { itemSelected } from '../../entities/selection';
import { newId } from '../../shared/lib/id';

/** Default category per template (only applied when the project has that category). */
export const TEMPLATE_CATEGORY: Record<TemplateId, DefaultCategoryKey | null> = {
  salary: 'salary',
  rentReceived: 'otherIncome',
  rentPaid: 'housing',
  utilities: 'utilities',
  livingCosts: 'food',
  subscription: 'leisure',
  bonus: 'salary',
  purchase: 'otherExpense',
  inheritance: 'otherIncome',
  savings: null,
  investment: null,
  property: null,
  mortgage: 'debt',
  consumerLoan: 'debt',
  incomeTax: 'taxes',
  salaryTaxBg: 'taxes',
  savingsRule: null,
};

/** Template picker layout. */
export const TEMPLATE_MENU: {
  group: 'income' | 'expense' | 'event' | 'asset' | 'liability' | 'rule';
  ids: TemplateId[];
}[] = [
  { group: 'income', ids: ['salary', 'rentReceived'] },
  { group: 'expense', ids: ['rentPaid', 'livingCosts', 'utilities', 'subscription'] },
  { group: 'event', ids: ['bonus', 'purchase', 'inheritance'] },
  { group: 'asset', ids: ['savings', 'investment', 'property'] },
  { group: 'liability', ids: ['mortgage', 'consumerLoan'] },
  { group: 'rule', ids: ['incomeTax', 'salaryTaxBg', 'savingsRule'] },
];

/** Builds a new item from a template for a variant (pure apart from the injected id). */
export function buildItem(
  project: Project,
  scenarioId: ScenarioId,
  templateId: TemplateId,
  name: string,
  id: ItemId,
  /** Localised name of preset deductions (social contributions). */
  contributionsName = 'Social contributions',
): Item {
  const key = TEMPLATE_CATEGORY[templateId];
  const categoryId = key ? defaultCategoryId(key) : undefined;
  const hasCategory =
    categoryId !== undefined && project.categories.some((c) => c.id === categoryId);
  const { start, horizonMonths } = project.assumptions;
  const item = TEMPLATES[templateId].create({
    id,
    name,
    ...(hasCategory ? { category: categoryId } : {}),
    taxContext: { start, horizonMonths, contributionsName },
  });
  if (templateId === 'salaryTaxBg' && item.type === 'formula') {
    // Tax the salary category when the project has it (bonuses included), else all income.
    const salary = defaultCategoryId('salary');
    if (project.categories.some((c) => c.id === salary)) {
      return {
        ...item,
        spec: { ...item.spec, sources: [{ ref: { type: 'category', id: salary }, sign: 1 }] },
      };
    }
  }
  if (templateId === 'savingsRule' && item.type === 'formula') {
    // Save into the variant's first asset account, if there is one.
    const scenario = project.scenarios.find((s) => s.id === scenarioId);
    const account = scenario?.itemIds.find((i) => project.items[i]?.type === 'asset');
    if (account) return { ...item, account };
  }
  return item;
}

export const addItemRequested = createEvent<{
  scenarioId: ScenarioId;
  templateId: TemplateId;
  name: string;
  contributionsName?: string;
}>();

const createItemFx = attach({
  source: $project,
  effect: (
    project,
    {
      scenarioId,
      templateId,
      name,
      contributionsName,
    }: { scenarioId: ScenarioId; templateId: TemplateId; name: string; contributionsName?: string },
  ) => {
    if (!project) throw new Error('No project open');
    const item = buildItem(
      project,
      scenarioId,
      templateId,
      name,
      newId('itm') as ItemId,
      contributionsName,
    );
    return { item, scenarioId };
  },
});

sample({ clock: addItemRequested, target: createItemFx });
sample({
  clock: createItemFx.doneData,
  fn: ({ item, scenarioId }) => ({ command: { type: 'item/add' as const, item, scenarioId } }),
  target: commandIssued,
});
sample({
  clock: createItemFx.doneData,
  fn: ({ item, scenarioId }) => ({ itemId: item.id, scenarioId }),
  target: itemSelected,
});
